import { Injectable } from '@angular/core';
import { ApolloQueryResult, OperationVariables } from '@apollo/client/core';
import { CompoundFilter, Filter, IqlQuery, IqlQueryOperator, IqlSort, IqlSortOperator } from '@itonics/filters';
import { AggregationQuery, AggregationResult } from '@itonics/types';
import { Observable } from 'rxjs';
import { GqlService } from '../../../../../core/src/lib/services/gql.service';
import { LogService } from '../../../../../logger/src';
import { getAggregation } from '../../graphql/queries/getAggregation';

const SORT_BY_RELEVANCE: string = '_score';
const ELASTIC_SEARCH_SORT_PARAM: string = '_id';

@Injectable({
  providedIn: 'root'
})
export class AggregationService {
  constructor(
    private readonly graphqlService: GqlService,
    private readonly logger: LogService
  ) {}

  /**
   * Get Aggregations Query
   *
   * @param aggregationQuery
   * @param filter
   * @returns Observable of array of aggregations
   */
  public fetchAggregationsFromGQL(
    spaceUri: string,
    aggregationQuery: Array<AggregationQuery>,
    filter: Filter
  ): Observable<Array<AggregationResult>> {
    // If sorting be relevance (_score), we need to provide a second
    // sorting param for elasticsearch (_id)
    // See CEL-294 for details
    const sorting: Array<IqlSort> = filter.getSort();
    if (sorting.length === 1 && sorting[0].by === SORT_BY_RELEVANCE) {
      sorting.push({ by: ELASTIC_SEARCH_SORT_PARAM, direction: IqlSortOperator.ASC });
      filter.setSort(sorting);
    }

    const filterQuery: IqlQuery =
      filter.toJson(true) ||
      new CompoundFilter(IqlQueryOperator.OR)
        .setIdentifier(filter.getIdentifier())
        // If there is no filter to use, just as for some empty shell filter with "newest elements first".
        // Use the sorting if available
        .setSort(sorting ?? [{ by: 'updatedOn', direction: IqlSortOperator.DESC }])
        .toJson(false);

    return new Observable((observer) => {
      this.graphqlService
        .watchQuery<{ elementTypeAggregations: Array<AggregationResult> }, OperationVariables>({
          query: getAggregation,
          variables: {
            request: {
              filterQuery,
              spaceUri,
              aggregationQuery
            }
          },
          fetchPolicy: 'network-only'
        })
        .subscribe({
          next: ({ data }: ApolloQueryResult<{ elementTypeAggregations: Array<AggregationResult> }>) => {
            if (!data) {
              this.logger.warn('Aggregations - no data');
              observer.next([]);
            } else {
              const aggregations: Array<AggregationResult> = data.elementTypeAggregations;
              observer.next(aggregations);
            }
            observer.complete();
          },
          error: (err) => {
            observer.error(err.message);
          }
        });
    });
  }
}
